import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';

const sectors = [
  {
    number: '01',
    icon: 'ri-heart-pulse-line',
    name: 'Health Technology',
    description:
      'Medical devices and intelligent monitoring systems designed to support faster intervention, stronger risk mitigation, and better outcomes across care environments.',
  },
  {
    number: '02',
    icon: 'ri-shield-cross-line',
    name: 'Safety & Care Infrastructure',
    description:
      'Platforms and systems that strengthen safety protocols and expand access to care — from fall detection in healthcare facilities to modern veterinary care delivery.',
  },
  {
    number: '03',
    icon: 'ri-barcode-box-line',
    name: 'Commerce & Industrial Systems',
    description:
      'RFID-based tracking and traceability solutions that bring real-time visibility and operational intelligence to industrial and commercial supply chains.',
  },
];

export default function SectorsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Sectors',
        item: `${siteUrl}/sectors`
      }
    ]
  };

  const sectorsSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Sectors | Diivy Focus Areas',
    url: `${siteUrl}/sectors`,
    description: 'Diivy focuses on three core sectors: Health Technology (fall detection, patient monitoring), Safety & Care Infrastructure (veterinary platforms), and Commerce & Industrial Systems (RFID tracking solutions).',
    about: [
      {
        '@type': 'Thing',
        name: 'Health Technology',
        description: 'Medical devices and monitoring systems for patient safety and care environments'
      },
      {
        '@type': 'Thing',
        name: 'Safety Technology',
        description: 'Fall detection and passive monitoring technology for healthcare facilities'
      },
      {
        '@type': 'Thing',
        name: 'Veterinary Care Infrastructure',
        description: 'Platforms supporting modern veterinary care delivery and commerce'
      },
      {
        '@type': 'Thing',
        name: 'Industrial Tracking Systems',
        description: 'RFID-based solutions for asset visibility and traceability'
      }
    ],
    keywords: 'health technology, medical technology, safety systems, veterinary care, RFID tracking, industrial solutions'
  };

  return (
    <>
      <SEOHead
        title="Sectors | Diivy Focus Areas - Health Technology, Safety & Commerce"
        description="Diivy focuses on three core sectors: Health Technology (fall detection, patient monitoring), Safety & Care Infrastructure (veterinary platforms), and Commerce & Industrial Systems (RFID tracking solutions)."
        keywords="health technology sectors, medical technology, safety systems, veterinary care, RFID solutions, industrial tracking, commerce platforms"
        canonical="/sectors"
        ogTitle="Sectors | Diivy Focus Areas - Health Technology, Safety & Commerce"
        ogDescription="Explore Diivy's focus areas: Health Technology, Safety & Care Infrastructure, and Commerce & Industrial Systems."
        ogUrl="/sectors"
        twitterTitle="Sectors | Diivy Focus Areas - Health Technology, Safety & Commerce"
        twitterDescription="Explore Diivy's focus areas: Health Technology, Safety & Care Infrastructure, and Commerce & Industrial Systems."
      />
      <JsonLd data={sectorsSchema} />
      <JsonLd data={breadcrumbSchema} />
      
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="pt-48 pb-32 bg-white">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Sectors
              </p>
            </div>

            <h1 className="text-[56px] lg:text-[68px] font-light text-[#0A0F1E] mb-10 leading-[1.1] tracking-[-0.025em]">
              Where Diivy <span className="font-semibold">Operates</span>
            </h1>

            <p className="text-[19px] text-gray-600 leading-[1.7] max-w-[720px] font-light">
              Diivy focuses on sectors where technology can deliver meaningful operational improvement.
            </p>
          </div>
        </section>

        {/* Sectors Grid */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="space-y-0">
              {sectors.map((sector, index) => (
                <div
                  key={index}
                  className="group grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-white/8 py-20 hover:border-[#C9A96E]/50 transition-all duration-700 cursor-default"
                >
                  {/* Number */}
                  <div className="lg:col-span-1 flex items-center">
                    <span className="text-[11px] font-semibold tracking-[0.2em] text-white/20 group-hover:text-[#C9A96E] transition-colors duration-500">
                      {sector.number}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className="lg:col-span-1 flex items-center">
                    <div className="w-11 h-11 flex items-center justify-center border border-white/12 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-500">
                      <i className={`${sector.icon} text-[17px] text-white/35 group-hover:text-[#0A0F1E] transition-colors duration-500`}></i>
                    </div>
                  </div>

                  {/* Name */}
                  <div className="lg:col-span-4 flex items-center">
                    <h3 className="text-[36px] font-light text-white group-hover:text-[#C9A96E] transition-colors duration-500 tracking-[-0.015em] leading-[1.2]">
                      {sector.name}
                    </h3>
                  </div>

                  {/* Description */}
                  <div className="lg:col-span-6 flex items-center">
                    <p className="text-[16px] text-white/45 leading-[1.85] group-hover:text-white/65 transition-colors duration-500 max-w-[560px] font-light">
                      {sector.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why These Sectors */}
        <section className="py-32 bg-white">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="flex items-end justify-between border-b border-[#0A0F1E]/8 pb-14 mb-20">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Investment Themes
                  </p>
                </div>
                <h2 className="text-[44px] font-light text-[#0A0F1E] tracking-[-0.02em] leading-[1.2]">
                  Common <span className="font-semibold">Threads</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-x divide-[#0A0F1E]/8">
              {[
                { label: 'Safety & Monitoring', icon: 'ri-shield-line' },
                { label: 'Care Infrastructure', icon: 'ri-heart-add-line' },
                { label: 'Intelligent Tracking', icon: 'ri-signal-tower-line' },
              ].map((theme, i) => (
                <div key={i} className="group px-12 first:pl-0 last:pr-0 space-y-8 cursor-default">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold tracking-[0.2em] text-[#C9A96E]">
                      0{i + 1}
                    </span>
                    <div className="w-11 h-11 flex items-center justify-center border border-[#0A0F1E]/12 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-500">
                      <i className={`${theme.icon} text-[17px] text-[#0A0F1E]/40 group-hover:text-white transition-colors duration-500`}></i>
                    </div>
                  </div>
                  <div className="w-full h-[1px] bg-[#0A0F1E]/8 group-hover:bg-[#C9A96E] transition-colors duration-700"></div>
                  <h3 className="text-[22px] font-semibold text-[#0A0F1E] tracking-[-0.015em] leading-[1.3]">{theme.label}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-[#0A0F1E]">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="flex items-center justify-between">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Get Started
                  </p>
                </div>
                <h2 className="text-[44px] font-light text-white tracking-[-0.02em] leading-[1.2]">
                  Ready to <span className="font-semibold">Transform</span> Your Operations?
                </h2>
              </div>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Contact Us
                  </p>
                </div>
                <Link to="/contact" className="text-[16px] text-white/45 hover:text-white/65 transition-colors duration-500">
                  Let's Talk
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}